import { inject, Injectable } from '@angular/core';
import {
  NzNotificationDataOptions,
  NzNotificationPlacement,
  NzNotificationService,
} from 'ng-zorro-antd/notification';
import { I18nService } from './i18n.service';

type NotificationType = 'success' | 'error' | 'info' | 'warning' | 'blank';

export interface NotificationOptions extends NzNotificationDataOptions {
  /** I18n key cho tiêu đề, nếu không truyền sẽ dùng tiêu đề mặc định theo type */
  titleKey?: string;
  /** Ghi đè vị trí hiển thị */
  placement?: NzNotificationPlacement;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly notification = inject(NzNotificationService);
  private readonly i18n = inject(I18nService);

  /** Cấu hình mặc định cho toàn bộ notification (style chung, duration, placement, ...) */
  private readonly baseConfig: NzNotificationDataOptions = {
    nzDuration: 3000,
    nzPlacement: 'topRight',
    nzPauseOnHover: true,
    // Tailwind + custom class để override giao diện mặc định của ng-zorro
    nzClass: [
      'app-notification', // class global, có thể style thêm trong SCSS
      'rounded-md',
      'shadow-lg',
      'border',
      'border-slate-200',
      'bg-white',
      'dark:border-slate-700',
      'dark:bg-slate-800',
    ].join(' '),
  };

  success(message: string, options?: NotificationOptions): void {
    this.show('success', message, options);
  }

  error(message: string, options?: NotificationOptions): void {
    this.show('error', message, options);
  }

  info(message: string, options?: NotificationOptions): void {
    this.show('info', message, options);
  }

  warning(message: string, options?: NotificationOptions): void {
    this.show('warning', message, options);
  }

  blank(message: string, options?: NotificationOptions): void {
    this.show('blank', message, options);
  }

  private show(type: NotificationType, message: string, options?: NotificationOptions): void {
    const title =
      (options?.titleKey && this.i18n.translate(options.titleKey)) || this.getDefaultTitle(type);

    const { placement, titleKey: _ignoreTitleKey, ...restOptions } = options ?? {};

    const baseClass = this.baseConfig.nzClass ?? '';
    const typeClass = this.getTypeClass(type);

    this.notification.create(type, title, message, {
      ...this.baseConfig,
      ...(placement ? { nzPlacement: placement } : {}),
      ...restOptions,
      nzClass: [baseClass, typeClass, restOptions.nzClass].filter(Boolean).join(' '),
    });
  }

  private getTypeClass(type: NotificationType): string {
    switch (type) {
      case 'success':
        return 'app-notification-success';
      case 'error':
        return 'app-notification-error';
      case 'warning':
        return 'app-notification-warning';
      case 'info':
        return 'app-notification-info';
      case 'blank':
      default:
        return 'app-notification-blank';
    }
  }

  private getDefaultTitle(type: NotificationType): string {
    switch (type) {
      case 'success':
        return this.i18n.translate('common.success') || 'Success';
      case 'error':
        return this.i18n.translate('common.error') || 'Error';
      case 'info':
        return this.i18n.translate('common.info') || 'Information';
      case 'warning':
        return this.i18n.translate('common.warning') || 'Warning';
      case 'blank':
      default:
        return this.i18n.translate('common.notification') || 'Notification';
    }
  }
}
